import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Notiflix from 'notiflix';
import PageMeta from '../components/SEO/PageMeta';
import StructuredData from '../components/SEO/StructuredData';

const RequestDemo = () => {
    const { t } = useTranslation();

    // State for form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [demoProducts, setDemoProducts] = useState([]);
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const demoOptions = [
        { key: 'data_collection', labelKey: 'solutions.data_collection.title' },
        { key: 'predictive_maintenance', labelKey: 'solutions.predictive_maintenance.title' },
        { key: 'ai_agents', labelKey: 'solutions.ai_agents.title' },
        { key: 'video_analytics', labelKey: 'solutions.video_analytics.title' },
        { key: 'ai_vision', labelKey: 'solutions.ai_vision.title' },
        { key: 'edge_ai', labelKey: 'solutions.edge_ai.title' },
        { key: 'dashboards', labelKey: 'solutions.dashboards.title' },
        { key: 'custom_integration', labelKey: 'solutions.custom_integration.title' },
    ];

    const handleChange = (e) => {
        const { value, checked } = e.target;
        if (checked) {
            setDemoProducts([...demoProducts, value]);
        } else {
            setDemoProducts(demoProducts.filter((item) => item !== value));
        }
    };

    const clearErrors = () => setErrors([]);
    const clearInput = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setMessage('');
        const checkboxes = document.querySelectorAll('input[type=checkbox]');
        checkboxes.forEach(checkbox => checkbox.checked = false);
        setDemoProducts([]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        let fData = new FormData();
        fData.append('first_name', firstName);
        fData.append('last_name', lastName);
        fData.append('email', email);
        fData.append('phone_number', phone);
        fData.append('message', message);
        fData.append('products', demoProducts.join(', '));

        axios({
            method: 'post',
            url: process.env.REACT_APP_DEMO_REQUEST_API,
            data: fData,
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(function (response) {
            clearInput();
            Notiflix.Report.success(
                t('demo_page.notifications.success_title'),
                response.data.message || t('demo_page.notifications.success_message'),
                'Okay'
            );
        })
        .catch(function (error) {
            const { response } = error;
            if (response && response.data && response.data.errors) {
                setErrors(response.data.errors);
            } else {
                 Notiflix.Report.failure(
                    t('demo_page.notifications.failure_title'),
                    error.message || t('demo_page.notifications.failure_message'),
                    'Okay'
                );
            }
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <>
            <PageMeta
                title={t('demo_page.doc_title')}
                description={t('demo_page.meta_description')}
                path="/request-demo"
            />
            <StructuredData type="page" page={{ name: t('nav.demo'), path: '/request-demo', kind: 'ContactPage', description: t('demo_page.meta_description') }} />

            <div id='demo' className="flex justify-center items-center w-full bg-white py-12 lg:py-16">
                <div className="container mx-auto my-8 px-4 lg:px-20" data-aos="zoom-in">
                    <form onSubmit={handleSubmit}>
                        <div className="w-full bg-white p-8 my-4 md:px-12 lg:w-9/12 lg:pl-20 lg:pr-40 mr-auto rounded-2xl shadow-2xl">
                            <div className="flex">
                                <h1 className="font-bold text-center lg:text-left text-primary uppercase text-4xl">{t('demo_page.title')}</h1>
                            </div>
                            <h2 className="text-xl text-gray-600 font-semibold my-4">{t('demo_page.subtitle')}</h2>

                            {demoOptions.map((option, index) => (
                                <div className="flex items-center my-4" key={option.key}>
                                    <input id={`checkbox-${index}`} type="checkbox" className="bg-gray-50 border-gray-300 focus:ring-3 focus:ring-primary/50 text-primary h-4 w-4 rounded" value={option.key} onChange={handleChange} />
                                    <label htmlFor={`checkbox-${index}`} className="ml-3 text-lg font-medium text-gray-900">{t(option.labelKey)}</label>
                                </div>
                            ))}
                            {errors.products && <p className="text-red-500 text-sm">{errors.products}</p>}

                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 mt-5">
                                <div>
                                    <input name="first_name" className="w-full bg-gray-100 text-gray-900 mt-2 p-3 rounded-lg focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-primary/50" type="text" placeholder={t('demo_page.form.first_name')} value={firstName} onChange={(e) => setFirstName(e.target.value)} onKeyUp={clearErrors} required />
                                    {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
                                </div>
                                <div>
                                    <input name="last_name" className="w-full bg-gray-100 text-gray-900 mt-2 p-3 rounded-lg focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-primary/50" type="text" placeholder={t('demo_page.form.last_name')} value={lastName} onChange={(e) => setLastName(e.target.value)} onKeyUp={clearErrors} required />
                                    {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
                                </div>
                                <div>
                                    <input name="email" className="w-full bg-gray-100 text-gray-900 mt-2 p-3 rounded-lg focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-primary/50" type="email" placeholder={t('demo_page.form.email')} value={email} onChange={(e) => setEmail(e.target.value)} onKeyUp={clearErrors} required />
                                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                    <input name="phone_number" className="w-full bg-gray-100 text-gray-900 mt-2 p-3 rounded-lg focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-primary/50" type="tel" placeholder={t('demo_page.form.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} onKeyUp={clearErrors} required />
                                    {errors.phone_number && <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>}
                                </div>
                            </div>
                            <div className="my-4">
                                <textarea name="message" placeholder={t('demo_page.form.message')} className="w-full h-32 bg-gray-100 text-gray-900 mt-2 p-3 rounded-lg focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-primary/50" value={message} onChange={(e) => setMessage(e.target.value)} onKeyUp={clearErrors} required></textarea>
                                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                            </div>
                            <div className="my-2 w-1/2 lg:w-2/4">
                                <button type="submit" disabled={isSubmitting} className="uppercase text-sm font-bold tracking-wide bg-primary hover:bg-primary-hover text-gray-100 p-3 rounded-lg w-full focus:outline-none focus:shadow-outline disabled:opacity-75">
                                    {isSubmitting ? t('demo_page.form.loading_button') : t('demo_page.form.submit_button')}
                                </button>
                            </div>
                        </div>
                    </form>

                    <div className="w-full lg:-mt-96 lg:w-2/6 px-8 py-6 ml-auto bg-primary rounded-2xl">
                        <div className="flex flex-col text-white">
                            <h2 className="font-bold uppercase text-2xl my-4">{t('demo_page.sidebar.title')}</h2>
                            <div className="flex flex-col my-4 space-y-4">
                                <div>
                                    <h3 className="text-xl font-semibold">{t('footer.company_info.address_label')}</h3>
                                    <p className="text-gray-200">{t('footer.company_info.address_value')}</p>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold">{t('footer.company_info.szekhely_label')}</h3>
                                    <p className="text-gray-200">{t('footer.company_info.szekhely_value')}</p>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold">{t('demo_page.sidebar.email_label')}</h3>
                                    <p className="text-gray-200">{t('footer.company_info.email')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RequestDemo;